import { db } from '../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { farmingResultsService } from './farmingResultsService';

export interface SeedEntryData {
  asin: string;
  campaign: string;
  seed: string;
  clicks: number | string;
  spend: number | string;
  sales: number | string;
  orders: number | string;
  costPerCst: number | string;
  currentStatus: string;
  overrideStatus: string;
  ppcStatus: string;
}

export interface ProcessedSeedData {
  seed: string;
  clicks: number;
  spend: number;
  sales: number;
  orders: number;
  costPerCst: number | null;
  amountOverBudget: number | null;
  amountOverBudgetPercent: number | null;
  cpc: number | null;
}

export interface SeedsToKillSummary {
  seeds: ProcessedSeedData[];
  totalSeeds: number;
}

interface SeedsToKillFirestoreData {
  asin?: string;
  companyName?: string;
  seedEntries?: SeedEntryData[];
  lastUpdated?: any;
  source?: string;
}

const isSeedsToKillData = (data: unknown): data is SeedsToKillFirestoreData => {
  return typeof data === 'object' && data !== null && 'seedEntries' in data && Array.isArray((data as any).seedEntries);
};

const parseNumericValue = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const fetchFinalCostPerCST = async (companyName: string, asin: string): Promise<number | null> => {
  try {
    const processedMetrics = await farmingResultsService.getProcessedFarmingMetrics(companyName, asin);
    if (!processedMetrics || !processedMetrics.finalCostPerCST) return null;
    
    const finalCostStr = processedMetrics.finalCostPerCST.replace('$', '').replace(',', '');
    const finalCost = parseFloat(finalCostStr);
    return isNaN(finalCost) ? null : finalCost;
  } catch (error) {
    console.warn(`Could not fetch Final $/CST for ${companyName}/${asin}:`, error);
    return null;
  }
};

export const fetchSeedsToKill = async (companyName: string, asin: string): Promise<SeedsToKillSummary> => {
  try {
    console.log(`Fetching Seeds to Kill for company: ${companyName}, ASIN: ${asin}`);
    
    const processedSeeds: ProcessedSeedData[] = [];
    
    const finalCostPerCST = await fetchFinalCostPerCST(companyName, asin);
    
    try {
      const dataCollectionRef = collection(db, 'pmt3_seeds_to_kill', companyName, 'data');
      const querySnapshot = await getDocs(dataCollectionRef);
      
      if (querySnapshot.empty) {
        console.warn(`No Seeds to Kill data found for ${companyName}`);
        return {
          seeds: [],
          totalSeeds: 0
        };
      }
      
      querySnapshot.forEach(docSnap => {
        const data = docSnap.data();
        
        if (isSeedsToKillData(data)) {
          const docAsin = data.asin || docSnap.id;
          
          if (docAsin === asin && data.seedEntries) {
            data.seedEntries.forEach(entry => {
              if (entry.currentStatus && entry.currentStatus.trim() !== '' && 
                  entry.overrideStatus !== 'Paused') {
                
                const clicks = parseNumericValue(entry.clicks);
                const spend = parseNumericValue(entry.spend);
                const sales = parseNumericValue(entry.sales);
                const orders = parseNumericValue(entry.orders);
                const costPerCst = parseNumericValue(entry.costPerCst);
                
                let amountOverBudget: number | null = null;
                let amountOverBudgetPercent: number | null = null;
                let cpc: number | null = null;
                
                if (costPerCst !== 0 && finalCostPerCST !== null) {
                  amountOverBudget = costPerCst - finalCostPerCST;
                  
                  if (amountOverBudget !== null && costPerCst !== 0) {
                    amountOverBudgetPercent = (amountOverBudget / costPerCst) * 100;
                  }
                }
                
                if (clicks !== 0) {
                  cpc = spend / clicks;
                }
                
                processedSeeds.push({
                  seed: entry.seed || '',
                  clicks: clicks,
                  spend: spend,
                  sales: sales,
                  orders: orders,
                  costPerCst: costPerCst !== 0 ? costPerCst : null,
                  amountOverBudget: amountOverBudget,
                  amountOverBudgetPercent: amountOverBudgetPercent,
                  cpc: cpc
                });
              }
            });
          }
        }
      });
      
      // Sort by clicks descending (highest clicks first)
      processedSeeds.sort((a, b) => b.clicks - a.clicks);
      
      console.log(`Found ${processedSeeds.length} seeds to possibly adjust for ${companyName}/${asin}`);
      
      return {
        seeds: processedSeeds,
        totalSeeds: processedSeeds.length
      };
      
    } catch (queryError) {
      console.error('Error querying Seeds to Kill:', queryError);
      return {
        seeds: [],
        totalSeeds: 0
      };
    }
    
  } catch (error) {
    console.error('Error fetching Seeds to Kill:', error);
    throw error;
  }
};

export const getAvailableCompaniesForSeedsToKill = async (): Promise<string[]> => {
  try {
    const collectionRef = collection(db, 'pmt3_seeds_to_kill');
    const querySnapshot = await getDocs(collectionRef);
    
    const companies: string[] = [];
    querySnapshot.forEach(doc => {
      companies.push(doc.id);
    });
    
    return companies.sort();
  } catch (error) {
    console.error('Error fetching available companies for seeds to kill:', error);
    return [];
  }
};